import React from 'react';
import { Link } from 'react-router-dom';
import { Navbar } from '../components/Navbar';
import { ClompApi } from '../api/ClompApi';
import { timeAgo } from '../api/presence';
import { Coins } from '../components/ClompCoin';
import { colorParts } from '../components/badgeColor';
import './AdminPage.css';

const api = new ClompApi();

// The panel's pages, listed down its side. Add new ones here and give them a render method below.
const SECTIONS = [
    { id: 'users', name: 'Users' },
    { id: 'badges', name: 'Badges' }
];

// How long after the colour picker last moved a badge's new colour is saved
const COLOR_SAVE_MS = 500;

// A badge's colour, for the .adm-badge style to draw it in
const badgeVars = (badge) => ({ '--badge': colorParts(badge.color) });

function formatDate(value) {
    return value ? new Date(value).toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' }) : '–';
}

export class AdminPage extends React.Component {

    state = {
        loading: true,
        // 'hidden' when the viewer isn't allowed in (the page then looks like any missing page), 'error' if it
        // couldn't load
        problem: null,
        open: false,
        // the viewer's own user id, to mark their row; null in open testing mode
        viewerID: null,
        levels: [],
        // the site's badges ([{ id, name, color }]), whether the viewer may manage them, and what a new one may be
        // ({ nameMaxLength, most, defaultColor })
        badges: [],
        canBadge: false,
        badgeLimits: null,
        // The Badges page: the new badge being written, the colours being picked for existing ones before they are
        // saved ({ badgeID: color }), the id of the badge whose Remove button is waiting to be confirmed, whether a
        // change is being saved, and the last thing that went wrong ({ field, message })
        newBadgeName: '',
        newBadgeColor: '#b0e0e6',
        badgeDrafts: {},
        confirmingBadge: null,
        savingBadge: false,
        badgeError: null,
        users: [],
        search: '',
        // userID -> 'saving' | 'saved' | 'failed'
        status: {},
        // the id of the user whose Delete button has been pressed and is waiting to be confirmed, the one being
        // deleted, and the last thing that went wrong with a delete
        confirmingDelete: null,
        deleting: null,
        deleteError: null,
        // The details window: the id of the user it is open for (null when closed), what the backend sent of them
        // ({ user, limits }, null while loading), whether that failed, what its username and biography boxes say,
        // and how saving those is going ({ field, message } when refused)
        detailsFor: null,
        details: null,
        detailsFailed: false,
        editUsername: '',
        editBio: '',
        savingProfile: false,
        profileSaved: false,
        profileError: null
    };

    componentDidMount() {
        let hadLogin = localStorage.getItem('token') !== null;
        api.admin.getUsers().then((data) => {
            this.setState({ loading: false, open: data.open, viewerID: data.viewerID, levels: data.levels, badges: data.badges || [], canBadge: Boolean(data.canBadge), users: data.users,
                badgeLimits: data.badgeLimits || null, newBadgeColor: (data.badgeLimits && data.badgeLimits.defaultColor) || '#b0e0e6'
            });
        }).catch((err) => {
            let code = err.response && err.response.status;
            // Nothing here should hint that an admin panel lives at this address: logged-out visitors go straight
            // to the login page, and accounts without access see the same thing as any page that doesn't exist.
            if (code === 401) {
                // A login that has run out comes back here after logging in again; someone who was never logged in
                // gets the plain login page.
                this.props.history.replace(hadLogin ? '/login?next=/admin' : '/login');
                return;
            }
            if (code !== 403) {
                console.log('Error loading page: ', err);
            }
            this.setState({ loading: false, problem: code === 403 ? 'hidden' : 'error' });
        });
    }

    componentWillUnmount() {
        window.removeEventListener('keydown', this.onKeyDown);
        Object.values(this.colorTimers).forEach(clearTimeout);
    }

    // badgeID -> the timer that will save its newly picked colour
    colorTimers = {};

    // What went wrong with a change to the site's badges, from the backend's refusal
    badgeFailed = (err) => {
        console.log('Error changing badges: ', err);
        let data = err.response && err.response.data;
        this.setState({ savingBadge: false, badgeError: { field: (data && data.field) || null, message: (data && data.message) || "Couldn't save that. Please try again." } });
    }

    addBadge = (e) => {
        e.preventDefault();
        let name = this.state.newBadgeName.trim();
        if (!name) {
            this.setState({ badgeError: { field: 'name', message: 'Give the badge a name' } });
            return;
        }
        this.setState({ savingBadge: true, badgeError: null });
        api.admin.addBadge(name, this.state.newBadgeColor).then((result) => {
            this.setState({ savingBadge: false, badges: result.badges, newBadgeName: '' });
        }).catch(this.badgeFailed);
    }

    // A colour has been picked for a badge: show it straight away, and save it once the picker has stopped moving
    pickBadgeColor(badge, color) {
        this.setState((s) => ({ badgeDrafts: { ...s.badgeDrafts, [badge.id]: color }, badgeError: null }));
        clearTimeout(this.colorTimers[badge.id]);
        this.colorTimers[badge.id] = setTimeout(() => {
            api.admin.setBadgeColor(badge.id, color).then((result) => {
                this.setState((s) => {
                    let badgeDrafts = { ...s.badgeDrafts };
                    // unless another colour has been picked since
                    if (badgeDrafts[badge.id] === color) {
                        delete badgeDrafts[badge.id];
                    }
                    return { badges: result.badges, badgeDrafts };
                });
            }).catch((err) => {
                this.setState((s) => {
                    let badgeDrafts = { ...s.badgeDrafts };
                    delete badgeDrafts[badge.id];
                    return { badgeDrafts };
                });
                this.badgeFailed(err);
            });
        }, COLOR_SAVE_MS);
    }

    deleteBadge(badge) {
        this.setState({ savingBadge: true, badgeError: null });
        api.admin.deleteBadge(badge.id).then((result) => {
            this.setState((s) => ({
                savingBadge: false, confirmingBadge: null, badges: result.badges,
                // it has gone from everyone who had it
                users: s.users.map((u) => ({ ...u, badges: (u.badges || []).filter((id) => id !== badge.id) }))
            }));
        }).catch((err) => {
            this.setState({ confirmingBadge: null });
            this.badgeFailed(err);
        });
    }

    // The Badges page: the site's badges, each with how many users have it, its colour and a button to remove it,
    // and under them the form for a new one. Staff see the list; Admins and Founders can change it.
    renderBadgesPage() {
        let { badges, users, canBadge, badgeLimits, newBadgeName, newBadgeColor, badgeDrafts, confirmingBadge, savingBadge, badgeError } = this.state;
        let holders = (badge) => users.filter((user) => (user.badges || []).includes(badge.id)).length;
        let people = (count) => `${count} ${count === 1 ? 'user' : 'users'}`;
        let full = Boolean(badgeLimits && badges.length >= badgeLimits.most);
        return (
            <section>
                <div className="adm-head">
                    <h2>Badges</h2>
                </div>
                <p className="adm-note adm-badges-note">
                    Badges show beside a player's name on their profile. Give one to a user from their Details window on the <Link to="/admin/users">Users</Link> page.
                    {canBadge ? '' : ' Only Admins and Founders can add, recolour or remove badges.'}
                </p>
                {badgeError && badgeError.field === null ? <p className="adm-error" role="alert">{badgeError.message}</p> : null}
                {badges.length === 0 ?
                    <p className="adm-empty">The site has no badges yet.</p>
                    :
                    <ul className="adm-badge-list">
                        {badges.map((badge) => {
                            let color = badgeDrafts[badge.id] || badge.color;
                            let count = holders(badge);
                            return (
                                <li key={badge.id}>
                                    <span className="adm-badge-sample"><span className="adm-badge is-on" style={badgeVars({ color })}>{badge.name}</span></span>
                                    <span className="adm-badge-holders">{people(count)}</span>
                                    {confirmingBadge === badge.id ?
                                        <span className="adm-confirm" role="group" aria-label={`Remove the ${badge.name} badge?`}>
                                            <span className="adm-badge-warning">{count > 0 ? `Takes it from ${people(count)}.` : 'Nobody has it.'}</span>
                                            <button type="button" className="adm-delete adm-delete-sure" disabled={savingBadge} onClick={() => this.deleteBadge(badge)}>
                                                {savingBadge ? 'Removing...' : 'Remove for good'}
                                            </button>
                                            <button type="button" className="adm-cancel" disabled={savingBadge} onClick={() => this.setState({ confirmingBadge: null })}>Cancel</button>
                                        </span>
                                        :
                                        <span className="adm-actions">
                                            <input type="color" className="adm-color" aria-label={`Colour of the ${badge.name} badge`} title={canBadge ? 'Change the colour' : "You aren't allowed to change badges"}
                                                value={color} disabled={!canBadge} onChange={(e) => this.pickBadgeColor(badge, e.target.value)} />
                                            <button type="button" className="adm-delete" disabled={!canBadge || savingBadge} title={canBadge ? undefined : "You aren't allowed to change badges"}
                                                aria-label={`Remove the ${badge.name} badge`} onClick={() => this.setState({ confirmingBadge: badge.id, badgeError: null })}>Remove</button>
                                        </span>
                                    }
                                </li>
                            );
                        })}
                    </ul>
                }

                {canBadge ?
                    <form className="adm-badge-form" onSubmit={this.addBadge} noValidate>
                        <h3>New badge</h3>
                        <div className="adm-badge-fields">
                            <input type="text" aria-label="Name of the new badge" placeholder="Badge name" value={newBadgeName} disabled={savingBadge || full}
                                maxLength={badgeLimits ? badgeLimits.nameMaxLength : 24} aria-invalid={Boolean(badgeError && badgeError.field === 'name')}
                                onChange={(e) => this.setState({ newBadgeName: e.target.value, badgeError: null })} />
                            <input type="color" className="adm-color" aria-label="Colour of the new badge" title="Pick the colour" value={newBadgeColor} disabled={savingBadge || full}
                                onChange={(e) => this.setState({ newBadgeColor: e.target.value })} />
                            <button type="submit" className="adm-save" disabled={savingBadge || full || !newBadgeName.trim()}>{savingBadge ? 'Saving...' : 'Add badge'}</button>
                        </div>
                        <p className="adm-badge-preview">
                            Looks like <span className="adm-badge is-on" style={badgeVars({ color: newBadgeColor })}>{newBadgeName.trim() || 'Badge name'}</span>
                        </p>
                        {badgeError && badgeError.field !== null ? <p className="adm-error" role="alert">{badgeError.message}</p> : null}
                        {full ? <p className="adm-fact-note">The site has as many badges as it can ({badgeLimits.most}). Remove one to add another.</p> : null}
                    </form>
                    : null}
            </section>
        );
    }

    onKeyDown = (e) => {
        if (e.key === 'Escape' && this.state.detailsFor !== null && !this.state.savingProfile) {
            this.closeDetails();
        }
    }

    // Open the details window for a user, and fetch the rest of what there is to know of them
    openDetails(user) {
        window.addEventListener('keydown', this.onKeyDown);
        this.setState({ detailsFor: user.id, details: null, detailsFailed: false, profileError: null, profileSaved: false });
        api.admin.getUser(user.id).then((details) => {
            if (this.state.detailsFor === user.id) {
                this.setState({ details, editUsername: details.user.username, editBio: details.user.bio });
            }
        }).catch((err) => {
            console.log('Error loading user: ', err);
            if (this.state.detailsFor === user.id) {
                this.setState({ detailsFailed: true });
            }
        });
    }

    closeDetails = () => {
        window.removeEventListener('keydown', this.onKeyDown);
        this.setState({ detailsFor: null, details: null });
    }

    // Save the username and biography typed into the details window
    saveProfile = (e) => {
        e.preventDefault();
        let { details, detailsFor, editUsername, editBio } = this.state;
        let changes = {};
        if (editUsername.trim() !== details.user.username) {
            changes.username = editUsername.trim();
        }
        if (editBio !== details.user.bio) {
            changes.bio = editBio;
        }
        if (Object.keys(changes).length === 0) {
            return;
        }
        this.setState({ savingProfile: true, profileError: null, profileSaved: false });
        api.admin.editUser(detailsFor, changes).then((result) => {
            this.setState((s) => ({
                savingProfile: false, profileSaved: true,
                details: s.details ? { ...s.details, user: result.user } : s.details,
                editUsername: result.user.username, editBio: result.user.bio,
                // the list shows their name too
                users: s.users.map((u) => (u.id === result.user.id ? { ...u, username: result.user.username } : u))
            }));
        }).catch((err) => {
            let data = err.response && err.response.data;
            this.setState({ savingProfile: false, profileError: { field: (data && data.field) || null, message: (data && data.message) || "Couldn't save that. Please try again." } });
        });
    }

    setLevel(user, level) {
        let before = user.level;
        let mark = (state) => this.setState((s) => ({ status: { ...s.status, [user.id]: state } }));
        let show = (value, more) => this.setState((s) => ({ users: s.users.map((u) => (u.id === user.id ? { ...u, ...more, level: value } : u)) }));
        show(level);
        mark('saving');
        api.admin.setLevel(user.id, level).then((result) => {
            // what the viewer may do with this user can change with the user's level
            show(result.user.level, { canSet: result.user.canSet });
            mark('saved');
        }).catch((err) => {
            console.log('Error changing access level: ', err);
            show(before);
            mark('failed');
        });
    }

    // Give a user a badge, or take it away if they have it
    toggleBadge(user, badge) {
        let has = !(user.badges || []).includes(badge.id);
        let mark = (state) => this.setState((s) => ({ status: { ...s.status, [user.id]: state } }));
        let show = (badges) => this.setState((s) => ({ users: s.users.map((u) => (u.id === user.id ? { ...u, badges } : u)) }));
        let before = user.badges || [];
        show(has ? [...before, badge.id] : before.filter((id) => id !== badge.id));
        mark('saving');
        api.admin.setBadge(user.id, badge.id, has).then((result) => {
            show(result.user.badges);
            mark('saved');
        }).catch((err) => {
            console.log('Error changing badge: ', err);
            show(before);
            mark('failed');
        });
    }

    deleteUser(user) {
        this.setState({ deleting: user.id, deleteError: null });
        api.admin.deleteUser(user.id).then(() => {
            this.setState((s) => ({ users: s.users.filter((u) => u.id !== user.id), deleting: null, confirmingDelete: null }));
        }).catch((err) => {
            console.log('Error deleting account: ', err);
            let message = err.response && err.response.data && err.response.data.message;
            this.setState({ deleting: null, confirmingDelete: null, deleteError: `Couldn't delete ${user.username}${message ? `: ${message}` : '. Please try again.'}` });
        });
    }

    // The last cell of a user's row: a Delete button, which asks to be pressed again before anything is deleted
    renderDelete(user) {
        let { confirmingDelete, deleting, viewerID } = this.state;
        if (!user.canDelete) {
            let why = user.id === viewerID ? 'Your own account is deleted from your profile settings' : "You aren't allowed to delete this account";
            return <button type="button" className="adm-delete" disabled title={why}>Delete</button>;
        }
        if (confirmingDelete === user.id) {
            return (
                <span className="adm-confirm" role="group" aria-label={`Delete ${user.username}?`}>
                    <button type="button" className="adm-delete adm-delete-sure" disabled={deleting === user.id} onClick={() => this.deleteUser(user)}>
                        {deleting === user.id ? 'Deleting...' : 'Delete for good'}
                    </button>
                    <button type="button" className="adm-cancel" disabled={deleting === user.id} onClick={() => this.setState({ confirmingDelete: null })}>Cancel</button>
                </span>
            );
        }
        return <button type="button" className="adm-delete" disabled={deleting !== null} aria-label={`Delete ${user.username}'s account`} onClick={() => this.setState({ confirmingDelete: user.id, deleteError: null })}>Delete</button>;
    }

    // A user's access level, as a list to pick from, with how saving the last change to them went beside it
    renderLevel(user) {
        let { levels, status, viewerID } = this.state;
        // each user's canSet lists the levels the viewer may give them; the backend decides and enforces it
        let canEdit = (user.canSet || []).some((level) => level !== user.level);
        let lockReason = user.id === viewerID ? "You can't change your own access level" : "You aren't allowed to change this user's level";
        return (
            <>
                <select className={`adm-level adm-level-${user.level}`} aria-label={`Access level for ${user.username}`} value={user.level}
                    disabled={status[user.id] === 'saving' || !canEdit}
                    title={canEdit ? undefined : lockReason}
                    onChange={(e) => this.setLevel(user, Number(e.target.value))}>
                    {levels.map((level) => (
                        <option key={level.id} value={level.id} disabled={level.id !== user.level && !(user.canSet || []).includes(level.id)}>{level.name}</option>
                    ))}
                </select>
                <span className={`adm-status adm-status-${status[user.id] || 'none'}`} role="status">
                    {status[user.id] === 'saving' ? 'Saving...' : status[user.id] === 'saved' ? 'Saved' : status[user.id] === 'failed' ? "Couldn't save" : ''}
                </span>
            </>
        );
    }

    // A user's badges: a switch for each, lit when the user has it
    renderBadges(user) {
        let { badges, canBadge, status } = this.state;
        return (
            <span className="adm-badges" role="group" aria-label={`Badges for ${user.username}`}>
                {badges.map((badge) => {
                    let has = (user.badges || []).includes(badge.id);
                    return (
                        <button key={badge.id} type="button" className={`adm-badge${has ? ' is-on' : ''}`} style={badgeVars(badge)} aria-pressed={has}
                            disabled={!canBadge || status[user.id] === 'saving'}
                            title={!canBadge ? "You aren't allowed to change badges" : has ? `Take the ${badge.name} badge away` : `Give the ${badge.name} badge`}
                            onClick={() => this.toggleBadge(user, badge)}>{badge.name}</button>
                    );
                })}
            </span>
        );
    }

    // The details window, over the page: everything known of one user, and what the viewer may change of them.
    // Their level and badges come from the list (so a change shows in both at once); the rest is fetched when it opens.
    renderDetails() {
        let { users, detailsFor, details, detailsFailed, badges, canBadge, editUsername, editBio, savingProfile, profileSaved, profileError } = this.state;
        let user = users.find((u) => u.id === detailsFor);
        if (!user) {
            return null;
        }
        let more = details && details.user.id === user.id ? details.user : null;
        let limits = details ? details.limits : null;
        let fieldError = (field) => (profileError && profileError.field === field ? profileError.message : null);
        let changed = more && (editUsername.trim() !== more.username || editBio !== more.bio);
        let signIn = more ? [more.signIn.password ? 'Password' : null, more.signIn.google ? 'Google' : null].filter(Boolean) : [];
        return (
            <div className="adm-overlay" onMouseDown={(e) => e.target === e.currentTarget && !savingProfile && this.closeDetails()}>
                <div className="adm-details-window" role="dialog" aria-modal="true" aria-labelledby="adm-details-title">
                    <div className="adm-details-head">
                        <div>
                            <h2 id="adm-details-title">{user.username}</h2>
                            <p className="adm-details-sub">
                                User #{user.id} · <Link to={`/profile/${encodeURIComponent(user.username)}`}>View profile</Link>
                            </p>
                        </div>
                        <button type="button" className="adm-cancel" onClick={this.closeDetails}>Close</button>
                    </div>

                    <dl className="adm-facts">
                        <div><dt>Joined</dt><dd>{formatDate(user.joined)}</dd></div>
                        <div><dt>Last login</dt><dd>{user.lastLogin ? new Date(user.lastLogin).toLocaleString() : '–'}</dd></div>
                        <div>
                            <dt>Status</dt>
                            <dd>
                                {user.online ? 'Online now' : user.lastSeen ? `Seen ${timeAgo(user.lastSeen)}` : 'Never seen'}
                                {user.presenceHidden ? ' (hidden from players)' : ''}
                            </dd>
                        </div>
                        {more ?
                            <>
                                <div><dt>Signs in with</dt><dd>{signIn.length > 0 ? signIn.join(' and ') : '–'}</dd></div>
                                <div><dt>Clomp Coins</dt><dd><Coins amount={more.coins.have} /> <span className="adm-fact-note">of {more.coins.earned.toLocaleString()} earned</span></dd></div>
                                <div>
                                    <dt>Cards</dt>
                                    <dd>{more.cards.different.toLocaleString()} unique <span className="adm-fact-note">· {more.cards.copies.toLocaleString()} total · {more.cards.packs.toLocaleString()} {more.cards.packs === 1 ? 'pack' : 'packs'} opened</span></dd>
                                </div>
                                <div>
                                    <dt>Achievements</dt>
                                    <dd>{more.achievements.map((game) => `${game.name} ${game.earned}/${game.total}`).join(' · ')}</dd>
                                </div>
                            </>
                            : null}
                    </dl>
                    {detailsFailed ? <p className="adm-error" role="alert">Couldn't load the rest of this user's details. Close this and try again.</p> : null}
                    {!more && !detailsFailed ? <p className="adm-note">Loading...</p> : null}

                    <h3>Access level</h3>
                    <div className="adm-details-row">{this.renderLevel(user)}</div>

                    {badges.length > 0 ?
                        <>
                            <h3>Badges</h3>
                            <div className="adm-details-row">{this.renderBadges(user)}</div>
                            <p className="adm-fact-note">{canBadge ? 'Click a badge to give it or take it away. It shows on their profile.' : 'Only Admins and Founders can change badges.'}</p>
                        </>
                        : null}

                    {more ?
                        <form onSubmit={this.saveProfile} noValidate>
                            <h3>Profile</h3>
                            {more.canEdit ? null : <p className="adm-fact-note">You aren't allowed to edit this user's profile.</p>}
                            <label htmlFor="adm-edit-username">Username</label>
                            <input id="adm-edit-username" type="text" value={editUsername} disabled={!more.canEdit || savingProfile} maxLength={limits.usernameMaxLength}
                                aria-invalid={Boolean(fieldError('username'))} onChange={(e) => this.setState({ editUsername: e.target.value, profileSaved: false })} />
                            {fieldError('username') ? <p className="adm-error" role="alert">{fieldError('username')}</p> : null}
                            <label htmlFor="adm-edit-bio">Biography</label>
                            <textarea id="adm-edit-bio" rows="4" value={editBio} disabled={!more.canEdit || savingProfile} maxLength={limits.bioMaxLength}
                                placeholder="No biography" aria-invalid={Boolean(fieldError('bio'))} onChange={(e) => this.setState({ editBio: e.target.value, profileSaved: false })} />
                            {fieldError('bio') ? <p className="adm-error" role="alert">{fieldError('bio')}</p> : null}
                            {profileError && profileError.field === null ? <p className="adm-error" role="alert">{profileError.message}</p> : null}
                            {more.canEdit ?
                                <div className="adm-details-actions">
                                    <span className="adm-status adm-status-saved" role="status">{profileSaved ? 'Saved' : ''}</span>
                                    <button type="submit" className="adm-save" disabled={!changed || savingProfile}>{savingProfile ? 'Saving...' : 'Save profile'}</button>
                                </div>
                                : null}
                        </form>
                        : null}
                </div>
            </div>
        );
    }

    renderUsers() {
        let { users, levels, badges, search, viewerID } = this.state;
        // each user's canSet lists the levels the viewer may give them; the backend decides and enforces it
        let wanted = search.trim().toLowerCase();
        let shown = users.filter((user) => !wanted || user.username.toLowerCase().includes(wanted) || String(user.id) === wanted);
        return (
            <section>
                <div className="adm-head">
                    <h2>Users</h2>
                    <input className="adm-search" type="search" aria-label="Search users" placeholder="Search by username or id"
                        value={search} onChange={(e) => this.setState({ search: e.target.value })} />
                </div>
                <ul className="adm-counts">
                    <li><strong>{users.length}</strong> signed up</li>
                    <li className="adm-count-online"><strong>{users.filter((user) => user.online).length}</strong> online now</li>
                    {levels.map((level) => (
                        <li key={level.id}><strong>{users.filter((user) => user.level === level.id).length}</strong> {level.name}</li>
                    ))}
                    {badges.map((badge) => (
                        <li key={badge.id}><strong>{users.filter((user) => (user.badges || []).includes(badge.id)).length}</strong> {badge.name}</li>
                    ))}
                </ul>
                {this.state.deleteError ? <p className="adm-error" role="alert">{this.state.deleteError}</p> : null}
                <div className="adm-table-wrap">
                    <table className="adm-table">
                        <thead>
                            <tr>
                                <th className="adm-id">ID</th>
                                <th>Username</th>
                                <th>Joined</th>
                                <th>Status</th>
                                <th>Access level</th>
                                <th>Account</th>
                            </tr>
                        </thead>
                        <tbody>
                            {shown.map((user) => (
                                <tr key={user.id}>
                                    <td className="adm-id">{user.id}</td>
                                    <td>
                                        <Link className="adm-user" to={`/profile/${encodeURIComponent(user.username)}`}>{user.username}</Link>
                                        {user.id === viewerID ? <span className="adm-you">You</span> : null}
                                    </td>
                                    <td>{formatDate(user.joined)}</td>
                                    <td>
                                        {user.online ?
                                            <span className="adm-presence is-online"><span className="adm-presence-dot" aria-hidden="true"></span>Online</span>
                                            :
                                            <span className="adm-presence" title={user.lastSeen ? new Date(user.lastSeen).toLocaleString() : undefined}>
                                                <span className="adm-presence-dot" aria-hidden="true"></span>{user.lastSeen ? `Seen ${timeAgo(user.lastSeen)}` : 'Never seen'}
                                            </span>
                                        }
                                        {user.presenceHidden ? <span className="adm-hidden" title="This user hides their status from other players">hidden</span> : null}
                                    </td>
                                    <td>{this.renderLevel(user)}</td>
                                    <td>
                                        <span className="adm-actions">
                                            <button type="button" className="adm-details" aria-label={`Details of ${user.username}`} onClick={() => this.openDetails(user)}>Details</button>
                                            {this.renderDelete(user)}
                                        </span>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
                {shown.length === 0 ?
                    <p className="adm-empty">{users.length === 0 ? 'Nobody has signed up yet.' : 'No users match that search.'}</p>
                    : null}
            </section>
        );
    }

    render() {
        let { loading, problem } = this.state;
        // which of the panel's pages the address names: /admin and /admin/users are the Users page
        let page = SECTIONS.find((section) => this.props.location.pathname.replace(/\/+$/, '').endsWith('/' + section.id)) || SECTIONS[0];
        // Until the backend has said the viewer may come in, show nothing that names the panel
        if (loading) {
            return <Navbar history={this.props.history}></Navbar>;
        }
        if (problem === 'hidden') {
            // the same as NotFoundPage
            return (
                <>
                    <Navbar history={this.props.history}></Navbar>
                    <div>
                        <h1>404: Page not found</h1>
                    </div>
                </>
            );
        }
        if (problem) {
            return (
                <>
                    <Navbar history={this.props.history}></Navbar>
                    <div className="adm-page">
                        <p className="adm-note">Couldn't load this page right now. Please try again later.</p>
                    </div>
                </>
            );
        }
        return (
            <>
                <Navbar history={this.props.history}></Navbar>
                <div className="adm-page">
                    <div className="adm-page-header">
                        <p className="adm-eyebrow">Clomp</p>
                        <h1>Admin panel</h1>
                    </div>
                    {this.state.open ?
                        <p className="adm-warning" role="note">
                            <strong>Testing mode:</strong> this panel is open to everyone, logged in or not. Never run the live server this way.
                        </p>
                        : null}
                    <div className="adm-layout">
                        <nav className="adm-nav" aria-label="Admin pages">
                            {SECTIONS.map((section) => (
                                <Link key={section.id} className={section.id === page.id ? 'is-active' : ''} aria-current={section.id === page.id ? 'page' : undefined} to={`/admin/${section.id}`}>{section.name}</Link>
                            ))}
                        </nav>
                        <div className="adm-main">
                            {page.id === 'badges' ? this.renderBadgesPage() : this.renderUsers()}
                        </div>
                        {this.state.detailsFor !== null ? this.renderDetails() : null}
                    </div>
                </div>
            </>
        );
    }

}
